import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import usePostMessageListener from './use-post-message-listener';

import {
  fetchPosts,
  fetchPostsSuccess,
  fetchPostsError,
  publicPostSubmission,
  searchForPosts,
  updatePostPolling,
  productsSelector,
  postsSelector,
  currentFiltersSelector,
  fetchNextPage,
} from '../modules/posts';
import {
  fetchTint,
  fetchTintError,
  fetchTintSuccess,
  selectTint,
  updateTintPersonalization,
  updateCustomFont,
  updateTint,
  CustomFontType,
} from '../modules/tint';
import { fetchAggregations, fetchAggregationsSuccess, fetchAggregationsError } from '../modules/aggregations';
import { getConfig } from '../modules/config';
import { updateMessages } from '../modules/messages';
import { TintWrapper } from '../services/tint-wrapper';
import { MessageService } from '../../../message-service';
import { getConfig as getUrlConfig } from '../../../url-reader';
import { globalConfig } from '../../../shared/config/config';
import Logger from '../../../shared/utils/logger';
import { PollingObserver } from '../../../shared/services/long-polling.service';
import { getUrlParamFilters } from '../utils/get-url-param-filters';

export default () => {
  const tint = TintWrapper.getTint();
  const dispatch = useDispatch();
  const tintSelector = useSelector(selectTint);
  const products = useSelector(productsSelector);
  const postList = useSelector(postsSelector);
  const currentFilters = useSelector(currentFiltersSelector);

  const urlConfig = getUrlConfig();

  const memoizedUsePostMessageListenerCallback = useCallback(
    data => {
      const {
        editorPersonalizationChanged,
        editorOpenedPublicPost,
        editorOpenedPopup,
        publicPostSubmitted,
        reviewSubmitted,
        closePopup,
        popupLoaded,
        changePostPopup,
        updatePopupData,
        fetchInfiniteScroll,
        recordEmbedLocation,
        recordEmbedViewed,
        tintAnalyticsScriptLoaded,
      } = globalConfig.postMessage.actions;
      // Popup Loaded is the event that informs tint about popup element already exist in the DOM
      // There should be only one popup element so the tint slug doesn't matter here
      if (data.tintSlug !== urlConfig.tintSlug && data.type !== popupLoaded) return;

      switch (data.type) {
        case editorPersonalizationChanged:
          dispatch(updateTintPersonalization(data.personalization));

          const draft = data?.personalization?.draft;
          if (draft?.team_id) {
            if (draft?.popup_font_id) {
              if (tintSelector?.personalization?.attributes?.data?.popup_font_id !== draft?.popup_font_id) {
                dispatch(updateCustomFont(draft.team_id, draft.popup_font_id, CustomFontType.CUSTOM_FONT_POPUP));
              }
            } else {
              dispatch(updateTint({ customFontPopup: null }));
            }

            if (draft?.font_secondary_id) {
              if (tintSelector?.personalization?.attributes?.data?.font_secondary_id !== draft?.font_secondary_id) {
                dispatch(
                  updateCustomFont(draft.team_id, draft.font_secondary_id, CustomFontType.CUSTOM_FONT_SECONDARY)
                );
              }
            } else {
              dispatch(updateTint({ customFontSecondary: null }));
            }
          }

          break;
        case editorOpenedPublicPost:
          dispatch(updateMessages({ key: 'isPublicPostOpenedFromEditor', value: true }));
          break;
        case editorOpenedPopup:
          dispatch(updateMessages({ key: 'isPopupOpenedFromEditor', value: true }));
          break;
        case reviewSubmitted:
          if (tintSelector && tintSelector.socialFeeds && tintSelector.socialFeeds.reviews) {
            dispatch(
              publicPostSubmission({
                socialFeedId: [tintSelector.socialFeeds.reviews[0].id],
                size: data.size,
                tag: urlConfig.tags,
                saved_filter_id: urlConfig.saved_filter_id,
                product_sku: urlConfig.product_sku,
              })
            );
          }
          break;
        case publicPostSubmitted:
          if (tintSelector && tintSelector.socialFeeds && tintSelector.socialFeeds.public_post) {
            dispatch(
              publicPostSubmission({
                socialFeedId: [tintSelector.socialFeeds.public_post[0].id],
                size: data.size,
                tag: urlConfig.tags,
                saved_filter_id: urlConfig.saved_filter_id,
                product_sku: urlConfig.product_sku,
              })
            );
          }
          break;
        case closePopup:
          if (data.transition) {
            dispatch(updateMessages({ key: 'isClosedByHighlightPostFeature', value: true }));
          } else {
            dispatch(updateMessages({ key: 'isPopupClosed', value: true }));
          }
          break;
        case popupLoaded:
          dispatch(updateMessages({ key: 'isPopupLoaded', value: true }));
          break;
        case fetchInfiniteScroll:
          dispatch<any>(fetchNextPage(false)).then(() => {
            dispatch(updateMessages({ key: 'didPaginate', value: true }));
            MessageService.send({
              type: globalConfig.postMessage.actions.postsAreFetched,
            });
          });
          break;
        case recordEmbedLocation:
          dispatch(updateMessages({ key: 'embedUrl', value: data.embedUrl }));
          break;
        case recordEmbedViewed:
          dispatch(updateMessages({ key: 'isEmbedVisible', value: true }));
          break;
        case changePostPopup:
          if (products.productTags.length === 0 && products.productList.length === 0) return;
          const currentPost = postList.find(post => post.id === data.postId);
          const productTagsIds =
            currentPost && currentPost.relationships.product_tags.data.map(productTag => productTag.id);

          const taggedProducts = productTagsIds ? getCurrentProducts(productTagsIds) : undefined;

          MessageService.send({
            type: updatePopupData,
            taggedProducts: taggedProducts,
            products: getProducts(taggedProducts),
          });
          break;
        case tintAnalyticsScriptLoaded:
          dispatch(updateMessages({ key: 'isAnalyticsLoaded', value: true }));

        default:
          break;
      }
    },
    [tintSelector, postList]
  );

  usePostMessageListener(memoizedUsePostMessageListenerCallback);

  const initTint = async (): Promise<void> => {
    dispatch(fetchPosts());
    dispatch(fetchTint());
    try {
      const {
        socialFeeds,
        postsResponse: { posts, postsRelationships, links },
        aggregations,
        meta,
        personalization,
        customFontSecondary,
        customFontPopup,
        pollingObserver,
      } = await tint.up();

      if (aggregations) {
        dispatch(fetchAggregations());
      }

      if (urlConfig.theme) {
        personalization.attributes.theme = urlConfig.theme;
      }

      if (!personalization) {
        Logger.error('There is no personalization data to be displayed');
      }

      if (
        ![
          'new_classic',
          'new_atmosphere',
          'new_slider',
          'video_slider',
          'new_square_photo',
          'new_tall_photo',
          'new_gem',
          'new_news',
          'email_template_grid',
          'email_template_collage',
          'table',
        ].includes(personalization.attributes.theme)
      ) {
        MessageService.send({
          type: globalConfig.postMessage.actions.loadOldTheme,
        });
      }
      if (personalization.attributes.theme === 'new_atmosphere') {
        await dispatch(
          searchForPosts(
            {
              size: urlConfig.count ? urlConfig.count : 160,
              tag: urlConfig.tags,
              saved_filter_id: urlConfig.saved_filter_id,
              product_sku: urlConfig.product_sku,
            },
            personalization?.attributes?.remove_duplicate_images
          )
        );
      } else if (
        personalization.attributes.theme === 'new_classic' &&
        personalization.attributes.data.waterfallToggle
      ) {
        await dispatch(
          searchForPosts(
            {
              size: urlConfig.count ? urlConfig.count : 100,
              tag: urlConfig.tags,
              saved_filter_id: urlConfig.saved_filter_id,
              product_sku: urlConfig.product_sku,
            },
            personalization?.attributes?.remove_duplicate_images
          )
        );
      } else if (personalization.attributes.theme === 'new_gem') {
        await dispatch(
          searchForPosts(
            {
              size: urlConfig.count ? urlConfig.count : 100,
              tag: urlConfig.tags,
              saved_filter_id: urlConfig.saved_filter_id,
              product_sku: urlConfig.product_sku,
            },
            personalization?.attributes?.remove_duplicate_images
          )
        );
      } else if (personalization.attributes.theme === 'table') {
        await dispatch(
          searchForPosts(
            {
              // If you want to fetch posts only with ratings
              // rating: { range: '1-100' }
              ...getUrlParamFilters(urlConfig),
              size: urlConfig.count ? urlConfig.count : globalConfig.defaultQueryCount,
              collapse: 'source,external_id',
            },
            personalization?.attributes?.remove_duplicate_images,
            ',replies'
          )
        );
      } else if (
        personalization.attributes.theme === 'email_template_collage' ||
        personalization.attributes.theme === 'email_template_grid'
      ) {
        await dispatch(
          searchForPosts({
            tag: urlConfig.tags,
            saved_filter_id: urlConfig.saved_filter_id,
            product_sku: urlConfig.product_sku,
            q: urlConfig.query,
          })
        );
      } else {
        dispatch(
          fetchPostsSuccess({
            posts,
            postsRelationships,
            links,
            isRemoveDuplicatePosts: personalization?.attributes?.remove_duplicate_images,
          })
        );
      }

      dispatch(
        fetchTintSuccess({
          meta,
          personalization,
          socialFeeds,
          customFontSecondary,
          customFontPopup,
        })
      );
      dispatch(getConfig());

      if (aggregations) {
        dispatch(fetchAggregationsSuccess(aggregations));
      }

      if (
        pollingObserver &&
        personalization.attributes.theme !== 'new_atmosphere' &&
        personalization.attributes.theme !== 'new_gem' &&
        personalization.attributes.data.waterfallToggle
      ) {
        await listenToPollEvents(pollingObserver);
      }
    } catch (error) {
      Logger.error(error);
      dispatch(fetchPostsError(error as Error));
      dispatch(fetchTintError(error as Error));
      dispatch(fetchAggregationsError(error as Error));
    }

    // Event for personalization builder to be sure that we send the draft
    // personalization after the theme one is fetched
    MessageService.send({
      type: globalConfig.postMessage.actions.personalizationFullyFetched,
    });
  };

  useEffect(() => {
    initTint();
  }, []);

  const listenToPollEvents = async (pollingObserver: PollingObserver) => {
    pollingObserver.subscribe(<IGetPostsReturnValue>(event) => {
      if (!currentFilters) {
        dispatch(updatePostPolling(event));
      }
    });
  };

  const getCurrentProducts = (productIDs: ReadonlyArray<string>) => {
    return mapProductList().filter(product => {
      return productIDs.find(id => id === product.productTagId);
    });
  };

  const mapProductList = () => {
    const assignTagsToProduct = products.productTags.map(productTag => ({
      ...productTag,
      product: products.productList.find(
        product => productTag.relationships.product.data && product.id === productTag.relationships.product.data.id
      ),
    }));

    return assignTagsToProduct.map(product => ({
      height: product.attributes.height,
      width: product.attributes.width,
      x: product.attributes.x,
      y: product.attributes.y,
      productId: product.relationships.product.data.id,
      productUrl: product.product ? product.product.attributes.url : null,
      productTagId: product.id,
    }));
  };

  const getProducts = taggedProducts => {
    return (
      taggedProducts &&
      taggedProducts
        .map(taggedProduct => {
          return products.productList.find(product => product.id === taggedProduct.productId);
        })
        .map(product => ({
          name: product.attributes.name,
          productImage: product.attributes.image_url,
          price: product.attributes.price,
          sku: product.attributes.sku,
          productUrl: product.attributes.url,
          currency: product.attributes.currency,
        }))
    );
  };
};
